import { useEffect, useRef } from 'react'
import Subscription, { ISubscription } from '@aytm/lib/common/subscription'
import { deepClone, deepEqual } from '@aytm/lib/common/object'

import loaderAction from '../../actions/loader/loaderAction'
import errorAction from '../../actions/error/errorAction'
import { addOnTryAgainToAction } from '../../actions/error/onTryAgain'

import ClientModule from '../../modules/client/Module'
import DevLog from '../../modules/devlog/Module'
import PreviewModule, { DEFAULT_PREVIEW_OPTIONS } from '../../modules/preview/Module'
import { JUMP_TO_START_OVER_ID } from '../../modules/preview/components/const'
import { DEFAULT_FEATURES } from './useFeaturesState'
import ThemeModule from '../../modules/theme/Module'

import { ISurveyWidgetSessionProps, ISurveyWidgetSessionPropsTheme } from '../../types'
import { ISessionAPI, ISessionUrls, ISessionUseAPIState, SessionElement } from '../types'
import { IAction, SurveyMode } from '../../actions/types'
import { QuestionID } from '../../questions/types'
import { IVideoAsset } from '../../modules/videos/types'
import { ActionType } from '@aytm/app/swidget/base/actions/types'

const useSessionAPIState: ISessionUseAPIState = (
  sessionProps,
  currentActionState,
  navigationState,
  nextButtonState,
  prevButtonState,
  currentLocalesState,
  widgetWidthState,
  progressState,
  featuresState,
  repeatVideosState,
  screenNumberState,
  customMessagesTextState,
  customStyleState,
  themeState,
  refreshView,
  currentActionComponentRef,
  _viewportRef,
  mountedRef
  // eslint-disable-next-line max-params
) => {
  const sessionElementRef = useRef<SessionElement>(null)
  const sessionAPIRef = useRef<ISessionAPI>(null)
  const sessionPropsRef = useRef<ISurveyWidgetSessionProps>(sessionProps)
  sessionPropsRef.current = sessionProps

  if (null == sessionAPIRef.current) {
    let currentTarget: QuestionID = ''
    let onUpdateSubscription: ISubscription<ISessionAPI> = null
    let currentQuota: number = null
    let currentLocale: string = null
    let currentUrls: ISessionUrls = null

    const sessionAPI: ISessionAPI = {
      get action() {
        return currentActionState.currentActionRef.current
      },
      get props() {
        return sessionPropsRef.current
      },
      doAction(action) {
        sessionAPI.clearRepeatVideos()
        currentActionState.setCurrentAction(action)
      },
      enableNavigation(enable) {
        sessionAPI.navigation?.enable(enable)
      },
      enableNext(enable) {
        if (sessionAPI.skipValidation) {
          // always enable next in xray mode
          if (!sessionAPI.navigation?.nextEnabled) {
            sessionAPI.navigation?.enableNext(true)
          }
        } else {
          sessionAPI.navigation?.enableNext(enable)
        }
      },
      enablePrev(enable) {
        sessionAPI.navigation?.enablePrev(enable)
      },
      showPrev(show) {
        sessionAPI.navigation?.showPrev(show)
      },
      get navigation() {
        return navigationState.navigationRef.current
      },
      doNext() {
        nextButtonState.onActionRef.current()
      },
      doPrev() {
        prevButtonState.onActionRef.current()
      },
      setLocales({ surveyLocaleID, interfaceLocaleID } = {}) {
        if (
          (surveyLocaleID && sessionAPI.surveyLocaleID !== surveyLocaleID) ||
          (interfaceLocaleID && sessionAPI.interfaceLocaleID !== interfaceLocaleID)
        ) {
          DevLog._log('SW[session]:', 'setLocales', { surveyLocaleID, interfaceLocaleID })
          currentLocalesState.setCurrentLocales(currentLocales => {
            return {
              surveyLocaleID: surveyLocaleID ? surveyLocaleID : currentLocales.surveyLocaleID,
              interfaceLocaleID: interfaceLocaleID ? interfaceLocaleID : currentLocales.interfaceLocaleID
            }
          })
        }
      },
      get surveyLocaleID() {
        return currentLocalesState.currentLocalesRef.current.surveyLocaleID
      },
      get interfaceLocaleID() {
        return currentLocalesState.currentLocalesRef.current.interfaceLocaleID
      },
      async changeSurveyMode(mode, target = currentTarget, fromAction = sessionAPI.action, apply = true) {
        let nextAction: IAction = null

        try {
          const withTarget = SurveyMode.RESPONDENT === mode ? 'q1' : target

          // show loader if request takes longer than a blink
          if (apply) {
            setTimeout(() => {
              if (mountedRef.current && null == nextAction) {
                currentActionState.setCurrentAction(loaderAction(deepClone({ ...fromAction, mode })))
              }
            }, 500)
          }

          nextAction = await ClientModule.requestSurveyModeChange(fromAction, mode, withTarget)
          if (ActionType.ERROR === nextAction.action) {
            addOnTryAgainToAction(nextAction, () => {
              void sessionAPI.changeSurveyMode(mode, target, fromAction, apply)
            })
          }

          if (mountedRef.current) {
            sessionAPI.clearRepeatVideos()
            if (SurveyMode.XRAYS === mode || nextAction.data?.back?.enable || !nextAction.data?.back?.isFirstBatch) {
              // disable screen number if we can go back after survey mode change or we are in xrays mode
              screenNumberState.disable()
            } else {
              // reset screen number otherwise
              screenNumberState.reset()
            }
            if (apply) {
              currentActionState.setCurrentAction(nextAction)
            }
          }

          return nextAction
        } catch (error) {
          DevLog.error(error, 'SW[session]:', 'changeSurveyMode')

          nextAction = errorAction(deepClone(fromAction), () => {
            void sessionAPI.changeSurveyMode(mode, target, fromAction, apply)
          })

          if (apply && mountedRef.current) {
            currentActionState.setCurrentAction(nextAction)
          }

          return nextAction
        }
      },
      setWidgetWidth(width) {
        widgetWidthState.setWidgetWidth(width)
        sessionAPI.update()
      },
      get progress() {
        return progressState.progressRef.current
      },
      get element() {
        return sessionElementRef.current
      },
      get document() {
        return sessionElementRef.current?.ownerDocument
      },
      get currentTarget() {
        return currentTarget
      },
      set currentTarget(target) {
        DevLog._log('SW[session]:', 'currentTarget =', target)
        currentTarget = target
      },
      get currentQuota() {
        return currentQuota
      },
      set currentQuota(quota) {
        DevLog._log('SW[session]:', 'currentQuota =', quota)
        currentQuota = quota
      },
      get currentLocale() {
        return currentLocale
      },
      set currentLocale(locale) {
        DevLog._log('SW[session]:', 'currentLocale =', locale)
        currentLocale = locale
      },
      get surveyMode() {
        return Object.values(SurveyMode).includes(sessionAPI.action?.mode)
          ? sessionAPI.action.mode
          : SurveyMode.RESPONDENT
      },
      set surveyMode(mode) {
        if (sessionAPI.surveyMode !== mode) {
          sessionAPI.changeSurveyMode(mode).catch(() => {
            // changeSurveyMode already catches errors
          })
        }
      },
      get urls() {
        return currentUrls
      },
      set urls(urls) {
        currentUrls = urls
      },
      get skipValidation() {
        return (
          SurveyMode.XRAYS === sessionAPI.surveyMode ||
          SurveyMode.BRIDGE === sessionAPI.surveyMode ||
          sessionAPI.previewOptions.suppressLogic
        )
      },
      async jumpToQuestion(target, fromAction = sessionAPI.action, mode = sessionAPI.surveyMode, apply = true) {
        let nextAction: IAction = null

        try {
          // show loader if request takes longer than a blink
          if (apply) {
            setTimeout(() => {
              if (mountedRef.current && null == nextAction) {
                currentActionState.setCurrentAction(loaderAction(deepClone(fromAction)))
              }
            }, 500)
          }

          nextAction = await ClientModule.requestJumpToQuestion(fromAction, mode, target)
          if (ActionType.ERROR === nextAction.action) {
            addOnTryAgainToAction(nextAction, () => {
              void sessionAPI.jumpToQuestion(target, fromAction, mode, apply)
            })
          }

          if (mountedRef.current) {
            sessionAPI.clearRepeatVideos()
            if (JUMP_TO_START_OVER_ID === target && SurveyMode.XRAYS !== mode) {
              // reset screen number if we jump to start over and we are not in xrays mode
              screenNumberState.reset()
            } else {
              // disable screen number otherwise
              screenNumberState.disable()
            }
            if (apply) {
              currentActionState.setCurrentAction(nextAction)
            }
          }

          return nextAction
        } catch (error) {
          DevLog.error(error, 'SW[session]:', 'jumpToQuestion')
          nextAction = errorAction(deepClone(fromAction), () => {
            void sessionAPI.jumpToQuestion(target, fromAction, mode, apply)
          })
          if (apply && mountedRef.current) {
            currentActionState.setCurrentAction(nextAction)
          }
          return nextAction
        }
      },
      async changeQuota(target) {
        let nextAction: IAction = null

        try {
          // show loader if request takes longer than a blink
          setTimeout(() => {
            if (mountedRef.current && null == nextAction) {
              currentActionState.setCurrentAction(loaderAction(deepClone(sessionAPI.action)))
            }
          }, 500)

          nextAction = await ClientModule.requestChangeQuota(sessionAPI.action, sessionAPI.surveyMode, target)

          if (mountedRef.current) {
            sessionAPI.clearRepeatVideos()
            screenNumberState.reset()
            currentActionState.setCurrentAction(nextAction)
            currentQuota = target
          }

          return nextAction
        } catch (error) {
          DevLog.error('SW[session]:', 'changeQuota', error)
          nextAction = errorAction(deepClone(sessionAPI.action))
          if (mountedRef.current) {
            currentActionState.setCurrentAction(nextAction)
          }
          return nextAction
        }
      },
      async setLocale(target) {
        let nextAction: IAction = null

        try {
          // show loader if request takes longer than a blink
          setTimeout(() => {
            if (mountedRef.current && null == nextAction) {
              currentActionState.setCurrentAction(loaderAction(deepClone(sessionAPI.action)))
            }
          }, 500)

          nextAction = await ClientModule.requestSetLocale(sessionAPI.action, sessionAPI.surveyMode, target)

          if (mountedRef.current) {
            sessionAPI.clearRepeatVideos()
            screenNumberState.reset()
            currentActionState.setCurrentAction(nextAction)
            currentLocale = target
          }

          return nextAction
        } catch (error) {
          DevLog.error('SW[session]:', 'setLocale', error)
          nextAction = errorAction(deepClone(sessionAPI.action))
          if (mountedRef.current) {
            currentActionState.setCurrentAction(nextAction)
          }
          return nextAction
        }
      },
      addVideoToRepeat(videoAsset) {
        if (null == repeatVideosState.repeatVideosRef.current) {
          repeatVideosState.repeatVideosRef.current = []
        }
        repeatVideosState.repeatVideosRef.current.push(videoAsset)
        DevLog._log('SW[session]:', 'addVideoToRepeat', videoAsset)
        repeatVideosState.setShowRepeatVideos(true)
      },
      removeVideoFromRepeat(videoAsset) {
        if (repeatVideosState.repeatVideosRef.current) {
          // remove last added video asset with the same video_type and video
          for (let i = repeatVideosState.repeatVideosRef.current.length - 1; i >= 0; i--) {
            const itemAsset = repeatVideosState.repeatVideosRef.current[i]
            if (
              itemAsset.video_type === videoAsset.video_type &&
              deepEqual<IVideoAsset>(itemAsset.video, videoAsset.video)
            ) {
              repeatVideosState.repeatVideosRef.current.splice(i, 1)
              break
            }
          }
          DevLog._log('SW[session]:', 'removeVideoFromRepeat', videoAsset)
          repeatVideosState.setShowRepeatVideos(Boolean(repeatVideosState.repeatVideosRef.current.length))
        }
      },
      clearRepeatVideos() {
        repeatVideosState.repeatVideosRef.current = []
        repeatVideosState.setShowRepeatVideos(false)
      },
      get repeatVideos() {
        return repeatVideosState.repeatVideosRef.current || []
      },
      get screenNumber() {
        return screenNumberState.screenNumberRef.current
      },
      get screenNumberDisabled() {
        return screenNumberState.disabled
      },
      set screenNumber(screenNumber) {
        if (!screenNumberState.disabled) {
          DevLog._log('SW[session]:', 'screenNumber =', screenNumber)
        }
        screenNumberState.setScreenNumber(screenNumber)
      },
      get baseScreenNumbersStack() {
        return screenNumberState.disabled ? [] : screenNumberState.baseScreenNumbersStackRef.current
      },
      get currentQuestionModel() {
        return currentActionComponentRef.current?.currentQuestionModel
      },
      get previewOptions() {
        return SurveyMode.RESPONDENT === sessionAPI.surveyMode ? DEFAULT_PREVIEW_OPTIONS : PreviewModule.previewOptions
      },
      get rules() {
        return sessionAPI.action?.data?.rules || {}
      },
      get features() {
        return featuresState.featuresRef.current || DEFAULT_FEATURES
      },
      get sideText() {
        return customMessagesTextState.sideTextRef.current || ''
      },
      get endText() {
        return customMessagesTextState.endTextRef.current || ''
      },
      get style() {
        return customStyleState.customStyleRef.current || ThemeModule.style
      },
      get themeClassNames() {
        let classNames =
          ISurveyWidgetSessionPropsTheme.LIGHT === sessionAPI.props.theme
            ? 'SWidgetPalette SWidgetPaletteL'
            : 'SWidgetPalette'
        classNames += ' PUmTheme PSWidgetL'
        classNames += themeState.customAccentColorCSSClassNameRef.current
          ? ` ${themeState.customAccentColorCSSClassNameRef.current}`
          : ''
        return classNames
      },
      applyCustomStyle(style) {
        customStyleState.setCustomStyle(currentCustomStyle =>
          style ? Object.assign({}, ThemeModule.style, currentCustomStyle || {}, style) : null
        )
      },
      scrollIntoView() {
        if (sessionAPI.element) {
          const sessionElementRect = sessionAPI.element.getBoundingClientRect?.()
          if (sessionElementRect?.top < 0) {
            sessionAPI.element.scrollIntoView?.({ block: 'start', behavior: 'auto', inline: 'center' })
          }
        }
      },
      refreshView() {
        refreshView()
      },
      update() {
        if (onUpdateSubscription) {
          onUpdateSubscription.update(sessionAPI)
        }
        return sessionAPI
      },
      onUpdate(listener) {
        if (null == onUpdateSubscription) {
          onUpdateSubscription = Subscription<ISessionAPI>()
        }
        return onUpdateSubscription.listen(listener)
      },
      clearSubscriptions() {
        if (null != onUpdateSubscription) {
          onUpdateSubscription.clear()
          onUpdateSubscription = null
        }
      }
    } as const

    sessionAPIRef.current = sessionAPI
  }

  useEffect(
    () => void sessionAPIRef.current?.update(),
    [
      sessionAPIRef.current.action,
      sessionAPIRef.current.navigation,
      sessionAPIRef.current.navigation?.loading,
      sessionAPIRef.current.surveyLocaleID,
      sessionAPIRef.current.interfaceLocaleID,
      sessionAPIRef.current.progress,
      sessionAPIRef.current.element,
      sessionAPIRef.current.currentTarget,
      sessionAPIRef.current.currentQuota,
      sessionAPIRef.current.surveyMode,
      sessionAPIRef.current.themeClassNames
    ]
  )

  useEffect(() => () => void sessionAPIRef.current?.clearSubscriptions(), [])

  return { sessionAPIRef, sessionElementRef }
}

export default useSessionAPIState
